import { api } from "@/lib/api";
import {
  AffiliateNote,
  AffiliateTransaction,
  AffiliateUser,
  StripeSubscriptionPlan,
} from "../schema";

export interface CreateAffiliateDto {
  email: string;
  name: string;
  password?: string;
}

export interface UpdateAffiliateDto {
  email?: string;
  name?: string;
  status?: string;
  affiliateCode?: string;
}

export interface InviteAffiliateDto {
  email: string;
  name: string;
}

export interface AffiliateStats {
  totalSignups: number;
  totalPurchases: number;
  totalCommission: number;
  commissionRate: number;
  affiliateCode: string;
}

export interface AffiliateReferral {
  id: string;
  user: {
    id: string;
    name: string;
    email: string;
    registeredAt: string;
  };
  status: "pending" | "converted" | "active" | "churned" | "paid";
  hasPurchased: boolean;
  firstPurchaseAt: string | null;
  totalEarnings: number;
  commissionRate: number;
  createdAt: string;
}

export class AffiliatesService {
  private static readonly BASE_URL = "/affiliate";

  static async getSubscriptionPlans(): Promise<StripeSubscriptionPlan[]> {
    return api.get<StripeSubscriptionPlan[]>("/subscription/plans");
  }

  static async getAffiliates(): Promise<AffiliateUser[]> {
    return api.get<AffiliateUser[]>(this.BASE_URL);
  }

  static async getAffiliate(id: string): Promise<AffiliateUser> {
    return api.get<AffiliateUser>(`${this.BASE_URL}/${id}`);
  }

  static async createAffiliate(
    data: CreateAffiliateDto,
    file?: File,
  ): Promise<AffiliateUser> {
    const formData = new FormData();
    formData.append("email", data.email);
    formData.append("name", data.name);
    if (data.password) formData.append("password", data.password);
    if (file) formData.append("avatar", file);

    return api.post<AffiliateUser>(this.BASE_URL, formData, true);
  }

  static async updateAffiliate(
    id: string,
    data: UpdateAffiliateDto,
    file?: File,
  ): Promise<AffiliateUser> {
    const formData = new FormData();
    if (data.email) formData.append("email", data.email);
    if (data.name) formData.append("name", data.name);
    if (data.status) formData.append("status", data.status);
    if (data.affiliateCode)
      formData.append("affiliateCode", data.affiliateCode);
    if (file) formData.append("avatar", file);

    return api.put<AffiliateUser>(`${this.BASE_URL}/${id}`, formData);
  }

  static async deleteAffiliate(id: string): Promise<{ message: string }> {
    return api.delete<{ message: string }>(`${this.BASE_URL}/${id}`);
  }

  static async restoreAffiliate(id: string): Promise<AffiliateUser> {
    return api.post<AffiliateUser>(`${this.BASE_URL}/${id}/restore`, {});
  }

  static async suspendAffiliate(id: string): Promise<AffiliateUser> {
    return api.post<AffiliateUser>(`${this.BASE_URL}/${id}/suspend`, {});
  }

  static async inviteAffiliate(
    data: InviteAffiliateDto,
  ): Promise<{ message: string; invitation: any }> {
    return api.post<{ message: string; invitation: any }>(
      `${this.BASE_URL}/invite`,
      data,
    );
  }

  static async getMyStats(): Promise<any> {
    return api.get<any>(`${this.BASE_URL}/my-stats`);
  }

  static async getNotes(affiliateId: string): Promise<AffiliateNote[]> {
    return api.get<AffiliateNote[]>(`${this.BASE_URL}/${affiliateId}/notes`);
  }

  static async addNote(
    affiliateId: string,
    content: string,
  ): Promise<AffiliateNote> {
    return api.post<AffiliateNote>(`${this.BASE_URL}/${affiliateId}/notes`, {
      content,
    });
  }

  static async setCommission(
    userId: string,
    commissionRate: number,
  ): Promise<{ commissionRate: number }> {
    return api.post<{ commissionRate: number }>(
      `${this.BASE_URL}/${userId}/commission`,
      { commissionRate },
    );
  }

  static async getAffiliateStats(affiliateId: string): Promise<AffiliateStats> {
    return api.get<AffiliateStats>(`${this.BASE_URL}/${affiliateId}/stats`);
  }

  static async getReferrals(affiliateId: string): Promise<AffiliateReferral[]> {
    return api.get<AffiliateReferral[]>(
      `${this.BASE_URL}/${affiliateId}/referrals`,
    );
  }

  static async getTransactions(
    affiliateId: string,
  ): Promise<AffiliateTransaction[]> {
    return api.get<AffiliateTransaction[]>(
      `${this.BASE_URL}/${affiliateId}/transactions`,
    );
  }

  static async sendReport(
    affiliateId: string,
    options?: {
      startDate?: string;
      endDate?: string;
      format?: "html" | "csv";
    },
  ): Promise<{
    message: string;
    sentTo: string;
    period: { startDate: string; endDate: string };
  }> {
    return api.post<{
      message: string;
      sentTo: string;
      period: { startDate: string; endDate: string };
    }>(`${this.BASE_URL}/${affiliateId}/send-report`, options || {});
  }
}
